import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getSupabaseFunctionErrorMessage } from "@/lib/supabase-function-errors";
import { ClonePollGate, clonePollDelay } from "./debugClonePolling";

type Env = "dev" | "prod";
type CloneJob = {
  job_id: string;
  status: string;
  phase: string | null;
  progress_percent: number | null;
  retry_after_seconds: number | null;
  transport_status: "completed" | "completed_with_warnings" | "transport_failure" | null;
  destination_app_status: string | null;
  transport_destination_app_status: string | null;
  destination_app_id: string | null;
  destination_url: string | null;
  runtime_readiness: "validating" | "ready" | "unavailable";
  security_blocked: boolean;
  security_reason: string | null;
  warnings: { code: string; message: string }[];
  failure: { code: string; message: string } | null;
};
type Source = { id: string; name: string; status: string };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const terminal = (job: CloneJob) => job.status === "failed" ||
  (["succeeded", "completed"].includes(job.status) && job.runtime_readiness !== "validating");
const MAX_VALIDATION_POLLS = 8;
const MAX_POLL_FAILURES = 5;

async function call<T>(action: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("copy-service-admin", { body: { action, ...body } });
  if (error || data?.error) {
    if (data?.security_blocked === true) throw new Error("Clone blocked for security.");
    throw new Error(data?.error?.message || await getSupabaseFunctionErrorMessage(error, "Clone request failed."));
  }
  return data as T;
}

export function DebugAppClone() {
  const [sourceEnv, setSourceEnv] = useState<Env>("prod");
  const [sourceId, setSourceId] = useState("");
  const [source, setSource] = useState<Source | null>(null);
  const [lookup, setLookup] = useState("");
  const [requestId, setRequestId] = useState<string | null>(null);
  const [job, setJob] = useState<CloneJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [pollFailures, setPollFailures] = useState(0);
  const [validationPolls, setValidationPolls] = useState(0);
  const pollGate = useRef(new ClonePollGate());
  const destinationEnv: Env = sourceEnv === "dev" ? "prod" : "dev";
  const validId = UUID.test(sourceId.trim());

  useEffect(() => {
    setSource(null);
    if (!validId || sourceEnv !== "dev") {
      setLookup(sourceEnv === "prod" && validId
        ? "PROD source details are checked by CopyService when the job starts."
        : "");
      return;
    }
    let cancelled = false;
    setLookup("Checking DEV App…");
    const timer = window.setTimeout(() => {
      void call<{ source: Source | null }>("lookup_debug_clone_source", {
        source_env: "dev", source_app_id: sourceId.trim(),
      }).then((data) => {
        if (cancelled) return;
        setSource(data.source);
        setLookup(data.source ? "" : "App not found in DEV. CopyService will verify the source on submission.");
      }).catch(() => {
        if (!cancelled) setLookup("DEV lookup unavailable. CopyService will verify the source on submission.");
      });
    }, 350);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [sourceEnv, sourceId, validId]);

  useEffect(() => {
    if (!job?.job_id || terminal(job) || validationPolls >= MAX_VALIDATION_POLLS || pollFailures >= MAX_POLL_FAILURES) return;
    let cancelled = false;
    const generation = pollGate.current.currentGeneration();
    const delay = clonePollDelay(job.retry_after_seconds, pollFailures);
    const timer = window.setTimeout(() => {
      const token = pollGate.current.start(job.job_id, generation);
      if (!token) return;
      void call<{ job: CloneJob }>("get_debug_clone_job", { job_id: job.job_id, source_app_id: sourceId.trim(), destination_env: destinationEnv })
        .then((data) => { if (!cancelled && pollGate.current.isCurrent(token)) {
          setPollFailures(0); setJob(data.job); setError("");
          if (data.job.runtime_readiness === "validating") setValidationPolls((count) => count + 1);
        } })
        .catch((reason) => { if (!cancelled && pollGate.current.isCurrent(token)) {
          setError(reason instanceof Error ? reason.message : "Job status unavailable.");
          setPollFailures((count) => count + 1);
        } })
        .finally(() => { pollGate.current.finish(token); });
    }, delay);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [job, pollFailures, validationPolls, destinationEnv, sourceId]);

  const submit = async () => {
    if (!validId || busy) return;
    if (!requestId && destinationEnv === "prod" && !window.confirm(
      `Create a debug destination App in PROD from DEV App ${sourceId.trim()}? The source App will not change.`,
    )) return;
    const id = requestId ?? crypto.randomUUID();
    setRequestId(id);
    setBusy(true);
    setError("");
    try {
      const data = await call<{ job: CloneJob }>("submit_debug_clone", {
        source_env: sourceEnv, source_app_id: sourceId.trim(), request_id: id,
      });
      setJob(data.job);
      setPollFailures(0);
      setValidationPolls(0);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Clone request failed. Retry with the same request ID.");
    } finally { setBusy(false); }
  };

  const refreshJob = async () => {
    if (!job || busy) return;
    const token = pollGate.current.start(job.job_id);
    if (!token) return;
    setBusy(true);
    try {
      const data = await call<{ job: CloneJob }>("get_debug_clone_job", { job_id: job.job_id, source_app_id: sourceId.trim(), destination_env: destinationEnv });
      if (pollGate.current.isCurrent(token)) {
        setJob(data.job);
        setPollFailures(0);
        setValidationPolls(0);
        setError("");
      }
    } catch (reason) {
      if (pollGate.current.isCurrent(token)) {
        setError(reason instanceof Error ? reason.message : "Job status unavailable.");
        if (!terminal(job)) setPollFailures((count) => count + 1);
      }
    } finally { pollGate.current.finish(token); setBusy(false); }
  };

  return <Card className="border-border/60">
    <CardHeader>
      <CardTitle className="text-base">Debug App Clone</CardTitle>
      <CardDescription>
        Broken, failed, and partial Apps are allowed. This operation is for debugging. The source App is never modified.
        Secrets, authentication, and integration credentials are not copied.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
        <div className="space-y-2">
          <Label htmlFor="clone-source-env">Source workspace</Label>
          <select id="clone-source-env" value={sourceEnv} disabled={Boolean(requestId)}
            onChange={(event) => setSourceEnv(event.target.value as Env)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="prod">PROD</option><option value="dev">DEV</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="clone-source-id">Source App ID</Label>
          <Input id="clone-source-id" placeholder="Applyt App UUID" value={sourceId}
            disabled={Boolean(requestId)} onChange={(event) => setSourceId(event.target.value)} />
        </div>
      </div>
      <p className="text-sm">Destination workspace: <strong>{destinationEnv.toUpperCase()}</strong></p>
      {source && <p className="text-sm">Source: <strong>{source.name}</strong> · {source.status} · <span className="break-all font-mono text-xs">{source.id}</span></p>}
      {lookup && <p className="text-xs text-muted-foreground" role="status">{lookup}</p>}
      <div className="flex flex-wrap gap-2">
        {!job && <Button disabled={!validId || busy} onClick={() => void submit()}>
          {busy ? "Submitting…" : requestId ? "Retry same request" : "Start debug clone"}
        </Button>}
        {job && <Button variant="outline" disabled={busy} onClick={() => void refreshJob()}>Refresh job</Button>}
        {requestId && <Button variant="ghost" disabled={busy} onClick={() => {
          pollGate.current.nextRequest();
          setRequestId(null); setJob(null); setError(""); setPollFailures(0); setValidationPolls(0);
        }}>New request</Button>}
      </div>
      {requestId && <p className="break-all text-xs">Request ID: <span className="font-mono">{requestId}</span></p>}
      {job && <div className="space-y-2 rounded-md border p-3 text-sm" role="status">
        <p className="break-all">Job ID: <span className="font-mono text-xs">{job.job_id}</span></p>
        <p>Progress: {job.status}{job.phase ? ` · ${job.phase.replaceAll("_", " ")}` : ""}{job.progress_percent !== null ? ` · ${job.progress_percent}%` : ""}</p>
        {terminal(job) && <>
          {job.security_blocked ? <p className="text-destructive">Clone blocked for security: {job.security_reason}</p>
            : <p>Clone status: <strong>{job.transport_status?.replaceAll("_", " ") ?? (job.status === "failed" ? "job failed" : "unreported")}</strong></p>}
          {!job.security_blocked && <p>CopyService destination status: <strong>{job.transport_destination_app_status ?? "unreported"}</strong></p>}
          {!job.security_blocked && <p>Destination runtime: <strong>{job.runtime_readiness === "ready" ? "Ready to open" : job.runtime_readiness === "validating" ? "Validating" : "Validation unavailable"}</strong></p>}
        </>}
        {!job.security_blocked && job.destination_app_id && <p className="break-all">Destination App ID: <span className="font-mono text-xs">{job.destination_app_id}</span></p>}
        {!job.security_blocked && job.runtime_readiness === "ready" && job.destination_url && <p><a className="text-primary underline" href={job.destination_url} target="_blank" rel="noopener noreferrer">Open destination App</a></p>}
        {job.runtime_readiness === "validating" && validationPolls >= MAX_VALIDATION_POLLS && <p>Runtime validation is still pending. Use Refresh job to check again.</p>}
        {pollFailures >= MAX_POLL_FAILURES && <p>Automatic status checks paused. Use Refresh job to retry.</p>}
        {job.warnings?.length > 0 && <div>Warnings:<ul className="ml-5 list-disc">{job.warnings.map((warning, index) => <li key={index}>{warning.message}</li>)}</ul></div>}
        {job.failure && !job.security_blocked && <p className="text-destructive">Diagnostic failure: {job.failure.message}</p>}
      </div>}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    </CardContent>
  </Card>;
}
